<script>
import widget from "../../Widget.vue";
import MargettsApi from "../../../modules/margetts-api";
import {nextTick, toRaw} from 'vue';

export default {
    name: "LumpSum",
    emits: ["emitLumpSum", "isLoading", "emitShareClass"],
    extends: widget,
    props: ["applicationFormValues"],
    data() {
        return {
            lumpSum: this.applicationFormValues.lumpSum?.lump_sum ?? null,
            shareClasses: [],
            preparedValues: [],
            shareClassesValues: [],
        }
    },

    created() {
      this.render();
    },

    methods: {
        render() {
            this.fetchShareClasses();

            if (this.applicationFormValues.lumpSum?.distribution) {
                for (const { id, amount } of JSON.parse(this.applicationFormValues.lumpSum.distribution)) {
                    this.shareClassesValues[id] = amount;
                }
            }
        },

        previous() {
            this.$emit('emitLumpSum', 3)
        },

        async fetchShareClasses() {
            this.$emit("isLoading", true);
            try {
                if (this.shareClasses.length === 0) {
                    const { data } = await axios.get('investment-application/share-class');
                    let responseData = data;
                    if ( ! responseData || ! responseData.result) {
                        return MargettsApi.throwNoResponseError();
                    }

                    if (responseData.result !== 'success') {
                        return this.throwApiError(responseData.messages);
                    }

                    this.shareClasses = responseData.data;
                    this.$emit('emitShareClass', this.shareClasses);
                }

                await nextTick();
            } catch(error) {
                MargettsApi.throwError(error);
            }
            this.$emit("isLoading", false);
        },

        async submit() {
            let isPercentageValid = this.validateShareClassesPercentage(this.shareClassesValues);
            if (! isPercentageValid) {
                this.throwValidationError()
                this.validationMessages = [];
                return;
            }

            const lumpSum = {
                lump_sum: this.lumpSum,
                distribution_method: 'Percentage',
                distribution: JSON.stringify(toRaw(this.preparedValues)),
            };

            try {
                this.$emit("isLoading", true);
                await this.submitLumpSum(lumpSum);
                this.$emit("isLoading", false);
                this.$emit('emitLumpSum', { page: 5, lumpSum: {...lumpSum}});
            } catch (error) {
                this.throwError(error);
            }
        },

        async submitLumpSum(data) {
            let formValues = {...data, application_id: this.applicationFormValues.applicationFormID};
            await MargettsApi.post('investment-application/investment', this.createQueryStringFromParameters(formValues, false), response => {
                if (!response || !response.result) {
                    return MargettsApi.throwNoResponseError();
                }

                if (response.result !== 'success') {
                    return this.throwApiError(response.messages);
                }

                /*const data = response.data;*/
            }).catch(error => {
                MargettsApi.throwError(error);
            });
        },

        validateShareClassesPercentage(data) {
            this.preparedValues = [];

            const MESSAGES = {
                not100: "Total percentage needs to be 100",
                overflowFrom: (shareClassName) =>
                    `Total percentage needs to be 100, please check values starting from ${shareClassName}`,
            };

            let totalPercentage = 0;
            let hasReportedOverflow = false;

            Object.values(this.shareClasses).forEach((value) => {
                const shareClassName = value?.name ?? "the first share class";

                for (const { id } of value.share_classes) {
                    const rawAmount = data[id];
                    if (rawAmount === undefined || rawAmount === null || rawAmount === "") continue;

                    const amount = Number.parseInt(rawAmount, 10);

                    this.preparedValues.push({ id, amount: rawAmount });
                    totalPercentage += amount;

                    if (totalPercentage > 100 && !hasReportedOverflow) {
                        this.addValidationMessage(MESSAGES.overflowFrom(shareClassName));
                        hasReportedOverflow = true;
                    }
                }
            });

            if (totalPercentage < 100) this.addValidationMessage(MESSAGES.not100);

            return totalPercentage === 100;
        },

        formatPercentage(value) {
            const percentage = value * 100;

            return this.roundHalfEven(percentage);
        },

        roundHalfEven(value, decimals = 2) {
            const factor = Math.pow(10, decimals);
            const n = value * factor;
            const f = Math.floor(n);
            const diff = n - f;

            if (diff > 0.5) return (f + 1) / factor;
            if (diff < 0.5) return f / factor;

            return (f % 2 === 0 ? f : f + 1) / factor;
        }
    }
}
</script>

<template>
    <section class="question" id="page4">
        <form @submit.prevent="submit" ref="dynamicForm">
            <div class="form-group share-class-lump-sum">
                <label for="name">
                    Lump Sum Amount (£)
                </label>

                <input
                    type="number"
                    class="form-control w-25"
                    name="lumpSum"
                    v-model="lumpSum"
                    required
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                >
            </div>

            <p class="shareClass-title"> Split across the below funds </p>

            <div class="share-class-row">
                <div v-for="shareClassGroup in shareClasses" class="shareClass-row-col-6">
                    <p class="shareClass-group">{{ shareClassGroup['group'] }}</p>
                    <span class="">(Initial charge: {{ formatPercentage(shareClassGroup['initial_charge']) }}%)</span>
                    <div v-for="shareClass in shareClassGroup['share_classes']" :key="shareClass.id" class="form-group form-row">
                        <label :for="shareClass.name">
                            {{ shareClass.name }}:
                        </label>
                        <input
                            type="number"
                            class="form-control w-25"
                            :name="shareClass.name"
                            v-model="shareClassesValues[shareClass.id]"
                            min="0"
                        >
                        <span class="badge badge-info badge-pill">%</span>
                    </div>
                </div>
            </div>

            <div class="form-group">
                <button type="submit" class="button button-primary">
                    <span>Next</span>
                </button>

                <button type="button" class="button" @click.stop="previous()">
                    Previous
                </button>
            </div>
        </form>
    </section>
</template>

<style scoped>
    .form-row {
        display: flex;
        align-items: center;
        gap: 1em;
        flex-direction: row;
        justify-content: flex-start;

        label {
            min-width: 55% !important;
            width: 55% !important;
            max-width: 55% !important;
        }
    }
    .w-25{
        min-width: 25% !important;
        width: 25% !important;
        max-width: 25% !important;
    }
    .badge-pill {
        background: darkgray;
        border-radius: 5px;
        padding: 5px;
        font-size: 18px;
    }

    input[type="number"] {
        text-align: right;
    }

    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
        -webkit-appearance: none;
    }

    input[type=number] {
        -moz-appearance: textfield;
    }

    .share-class-lump-sum {
        margin: 0.5em;
        display: flex;
        align-items: center;
        gap: 1em;
        justify-content: center;
    }

    .shareClass-title {
        margin: 0.5em;
        font-weight: 550;
    }

    .shareClass-group {
        font-weight: bold;
        margin-bottom: 0.1em !important;
    }

    .share-class-row {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        margin: 0.5em;

    }

    .shareClass-row-col-6 > .form-group:last-of-type {
        flex-direction: row !important;
        justify-content: flex-start !important;
        margin-top: 2em !important;
    }
</style>
